// The finding investigations the Findings console reads and writes: every
// investigation the reader can see (listed whole, a page of 25 at a time, up
// to MAX_PAGES), the people a case can be assigned to, and the decisions,
// each saved as version-checked writes and returned for Undo. A decision on a
// case that gained findings since its investigation was saved adds them
// first, so it covers what the reader saw; a duplicate's findings join the
// investigation it repeats.
//
// Reads scale with active analysts, not endpoints: one listing on open and
// again every REFRESH_MS while the page is visible, and one write per
// decision per case.
import { useCallback, useEffect, useRef, useState } from "react";
import { getFindingInvestigation, listAssignableUsers, listFindingInvestigations, listTenantUserNames, saveFindingInvestigation } from "./alertTriageClient.js";
import { ATTEMPTS, DECISION_NOTE, additionBatches, caseInvestigationId, changeFor, investigationFor, noteWith, periodFor } from "./findingsConsole.js";

export const MAX_PAGES = 40;
export const REFRESH_MS = 60_000;
const TITLE_MAX = 200;
const newOperationId = () => crypto.randomUUID();

// Created under an id a deleted investigation used: the next attempt's id.
const isRetiredId = (e) => e?.grpcCode === "FAILED_PRECONDITION" && /deleted investigation identity/i.test(e?.serverMessage || e?.message || "");
// Someone saved it since it was read.
const isStale = (e) => e?.grpcCode === "ABORTED" || e?.status === 409;
// An addition already taken back, by an earlier try or a teammate.
const isUndone = (e) => e?.grpcCode === "FAILED_PRECONDITION" && /no findings left to remove/i.test(e?.serverMessage || e?.message || "");
// The decisions that close an investigation, which first save what the case holds.
const CLOSES = new Set(["truePositive", "close", "join", "inconclusive"]);

const titleOf = (view) => (String(view.title || "").trim() || "Untitled case").slice(0, TITLE_MAX);
const snapshot = (inv) => (inv ? { status: inv.status, owner: inv.ownerUserId || null, verdict: inv.verdict || null, summary: inv.summary || "" } : null);
const NEW_CASE = { status: "open", owner: null, verdict: null, summary: "" };

function sameInvestigations(a, b) {
    if (a.size !== b.size) return false;
    for (const [id, inv] of b) if (!a.has(id) || JSON.stringify(a.get(id)) !== JSON.stringify(inv)) return false;
    return true;
}

// The findings of every open investigation, so a case someone opened stays
// listed whatever triage says of it now: one listing, then one read per open
// investigation, OPEN_READS at a time. Open investigations scale with
// analysts, not endpoints.
// ponytail: past OPEN_CASES_MAX open investigations the rest drop from the list; a listing that carried its findings would remove the per-investigation reads.
export const OPEN_CASES_MAX = 200;
const OPEN_READS = 6;
export async function listOpenCaseFindingIds({ signal } = {}) {
    const { byId } = await listAllInvestigations({ signal, status: "open" });
    const ids = [...byId.keys()].slice(0, OPEN_CASES_MAX);
    const out = new Set();
    for (let i = 0; i < ids.length; i += OPEN_READS) {
        const got = await Promise.all(ids.slice(i, i + OPEN_READS).map((id) => getFindingInvestigation(id, { signal }).catch(() => null)));
        for (const inv of got) for (const f of inv?.findings || []) if (f.finding_id) out.add(f.finding_id);
    }
    return [...out];
}

export async function listAllInvestigations({ signal, maxPages = MAX_PAGES, status = "" } = {}) {
    const byId = new Map();
    let token = "";
    let pages = 0;
    do {
        const page = await listFindingInvestigations({ pageToken: token, status, signal });
        for (const inv of page.investigations) byId.set(inv.incidentId, inv);
        token = page.nextPageToken;
        pages += 1;
    } while (token && pages < maxPages);
    return { byId, capped: !!token };
}

export function useConsoleInvestigations({ enabled = true, canWrite = false, status = "" } = {}) {
    const [state, setState] = useState({ status: enabled ? "loading" : "idle", byId: new Map(), error: null, capped: false });
    const [users, setUsers] = useState(() => new Map());
    const [names, setNames] = useState(() => new Map());
    const byIdRef = useRef(state.byId);
    byIdRef.current = state.byId;
    const [tick, setTick] = useState(0);
    const refresh = useCallback(() => setTick((n) => n + 1), []);
    useEffect(() => {
        globalThis.addEventListener?.("origin-investigation-saved", refresh);
        return () => globalThis.removeEventListener?.("origin-investigation-saved", refresh);
    }, [refresh]);

    useEffect(() => {
        if (!enabled) return undefined;
        const ac = new AbortController();
        let alive = true;
        let running = false;
        const run = () => {
            if (running) return;
            running = true;
            listAllInvestigations({ signal: ac.signal, status })
                .then(({ byId, capped }) => {
                    if (!alive) return;
                    setState((s) => (s.status === "done" && !s.error && s.capped === capped && sameInvestigations(s.byId, byId) ? s : { status: "done", byId, error: null, capped }));
                })
                .catch((error) => {
                    if (!alive || error?.name === "AbortError") return;
                    setState((s) => ({ ...s, status: s.status === "done" ? "done" : "error", error }));
                })
                .finally(() => { running = false; });
        };
        run();
        const timer = setInterval(() => { if (globalThis.document?.visibilityState !== "hidden") run(); }, REFRESH_MS);
        return () => { alive = false; ac.abort(); clearInterval(timer); };
    }, [enabled, tick, status]);

    useEffect(() => {
        if (!enabled || !canWrite) return undefined;
        let alive = true;
        listAssignableUsers()
            .then((list) => { if (alive) setUsers(new Map(list.map((u) => [u.userId, u.email]))); })
            .catch(() => { /* names fall back to "A teammate" */ });
        return () => { alive = false; };
    }, [enabled, canWrite]);

    const [wantNames, setWantNames] = useState(false);
    const loadNames = useCallback(() => setWantNames(true), []);
    useEffect(() => {
        if (!enabled || !canWrite || !wantNames) return undefined;
        let alive = true;
        // Listing the tenant's people needs admin access; without it a
        // teammate is searched for by the start of their email instead.
        listTenantUserNames()
            .then((found) => { if (alive) setNames(found); })
            .catch(() => {});
        return () => { alive = false; };
    }, [enabled, canWrite, wantNames]);

    const put = useCallback((inv) => {
        byIdRef.current = new Map(byIdRef.current).set(inv.incidentId, inv);
        setState((s) => ({ ...s, byId: new Map(s.byId).set(inv.incidentId, inv) }));
    }, []);

    // One version-checked write; read again and retried once when someone
    // else saved first. Returns the investigation saved and its operation.
    const save = useCallback(async (inv, change) => {
        const attempt = async (expectedVersion) => {
            const operationId = newOperationId();
            return { saved: await saveFindingInvestigation(inv.incidentId, { operationId, expectedVersion, ...change }), operationId };
        };
        try {
            return await attempt(inv.version);
        } catch (e) {
            if (!isStale(e)) throw e;
            return attempt((await getFindingInvestigation(inv.incidentId)).version);
        }
    }, []);
    const update = useCallback(async (inv, change) => (await save(inv, change)).saved, [save]);

    // A case's findings added to an investigation, ADD_LIMIT at a time, as one
    // batch a single Undo takes back; findings it already holds stay as they
    // were. The server changes findings only on an open investigation, so a
    // closed one reopens with the first batch (the decision that follows sets
    // where it ends). Returns the investigation saved and the operation to
    // undo, if any finding was new to it. A refusal partway carries what was
    // saved (`done`) for Undo.
    const addFindings = useCallback(async (inv, view, note) => {
        const period = periodFor(view.items);
        if (!period?.findingIds.length) return { saved: inv, undo: null };
        const batch = newOperationId();
        let saved = inv;
        let undo = null;
        try {
            for (const ids of additionBatches(period.findingIds)) {
                const done = await save(saved, {
                    addFindingIds: ids, timeRange: period.timeRange, source: { from: "findings-console", case: view.id, batch }, note,
                    ...(saved.status === "closed" ? { status: "open" } : {}),
                });
                saved = done.saved;
                undo ||= done.operationId;
                put(saved);
            }
        } catch (e) {
            if (undo) e.done = [{ id: inv.incidentId, previous: inv.status === "closed" ? snapshot(inv) : null, undo }];
            throw e;
        }
        return { saved, undo: saved.findingCount > inv.findingCount ? undo : null };
    }, [put, save]);

    /**
     * Records a decision on a case (`view`, a caseView): its investigation,
     * created on the first decision with every finding the case holds. A
     * decision that closes it saves the case's findings into it first, so the
     * verdict covers what the reader saw. Returns what Undo needs:
     * [{ id, previous, undo }]; a refusal carries what was saved (`done`).
     */
    const decide = useCallback(async (view, action, opts = {}) => {
        let inv = investigationFor(view.findingIds, byIdRef.current);
        const previous = snapshot(inv);
        if (inv) {
            let undo = null;
            try {
                if (CLOSES.has(action)) ({ saved: inv, undo } = await addFindings(inv, view, DECISION_NOTE.saved));
                const saved = await update(inv, changeFor(action, { ...opts, previous }));
                put(saved);
                return [{ id: saved.incidentId, previous, undo }];
            } catch (e) {
                if (!e.done && (undo || inv.status !== previous.status)) e.done = [{ id: inv.incidentId, previous, undo }];
                throw e;
            }
        }
        const period = periodFor(view.items);
        if (!period?.findingIds.length) throw new Error("This case has no reviewed findings to save.");
        const batches = additionBatches(period.findingIds);
        const change = changeFor(action, opts);
        const lead = view.findingIds[0];
        // The decision goes with the last batch: a closed investigation takes no more findings.
        const decisionAt = (i) => (i === batches.length - 1 ? change : {});
        let lastError = null;
        for (let n = 0; n < ATTEMPTS; n++) {
            const id = caseInvestigationId(lead, n);
            try {
                let saved = await saveFindingInvestigation(id, {
                    operationId: newOperationId(), expectedVersion: 0, addFindingIds: batches[0], timeRange: period.timeRange,
                    title: titleOf(view), source: { from: "findings-console", case: view.id }, ...decisionAt(0),
                });
                for (const [i, batch] of batches.slice(1).entries()) {
                    saved = await saveFindingInvestigation(id, {
                        operationId: newOperationId(), expectedVersion: saved.version, addFindingIds: batch, timeRange: period.timeRange,
                        source: { from: "findings-console", case: view.id }, ...decisionAt(i + 1),
                    });
                }
                put(saved);
                return [{ id, previous: NEW_CASE }];
            } catch (e) {
                lastError = e;
                if (isRetiredId(e)) continue;
                if (isStale(e)) {
                    // A teammate created this case's investigation first: the
                    // decision goes onto theirs.
                    const theirs = await getFindingInvestigation(id);
                    const saved = await update(theirs, changeFor(action, { ...opts, previous: snapshot(theirs) }));
                    put(saved);
                    return [{ id, previous: snapshot(theirs) }];
                }
                throw e;
            }
        }
        throw lastError;
    }, [put, update, addFindings]);

    /**
     * Records a case (`view`) as a duplicate of the open investigation that
     * records `target` (another case's view): its findings join that
     * investigation, and its own closes, naming it. Returns what Undo needs
     * for both; a refusal carries what was saved (`done`).
     */
    const join = useCallback(async (view, target, { me = null } = {}) => {
        const into = investigationFor(target.findingIds, byIdRef.current);
        if (into?.status !== "open") throw new Error("That investigation is no longer open. Reload to try again.");
        const { undo } = await addFindings(into, view, noteWith(DECISION_NOTE.added, titleOf(view)));
        const added = { id: into.incidentId, previous: null, undo };
        try {
            return [added, ...await decide(view, "join", { me, target: byIdRef.current.get(into.incidentId) || into })];
        } catch (e) {
            e.done = [added, ...(e.done || [])];
            throw e;
        }
    }, [addFindings, decide]);

    /**
     * Puts an investigation back where a decision found it, taking back the
     * findings it added (reopening a closed one for that, and closing it again
     * where the decision was not its own).
     */
    const revert = useCallback(async ({ id, previous, undo }) => {
        let inv = byIdRef.current.get(id) || await getFindingInvestigation(id);
        if (undo) {
            const closed = inv.status === "closed";
            try {
                inv = await update(inv, { undoOperationId: undo, ...(closed ? { status: "open" } : {}) });
                put(inv);
                if (closed && !previous) {
                    inv = await update(inv, { status: "closed" });
                    put(inv);
                }
            } catch (e) {
                if (!isUndone(e)) throw e;
            }
        }
        if (!previous) return inv;
        const saved = await update(inv, changeFor("revert", { previous }));
        put(saved);
        return saved;
    }, [put, update]);

    return { ...state, users, names, loadNames, decide, join, revert, refresh };
}

/** One investigation's activity, newest first, read again when its version changes. */
export function useInvestigationActivity(id, version) {
    const [read, setRead] = useState({ status: "idle", activity: [], error: null });
    useEffect(() => {
        if (!id) { setRead({ status: "idle", activity: [], error: null }); return undefined; }
        const ac = new AbortController();
        let alive = true;
        setRead((r) => ({ ...r, status: "loading" }));
        getFindingInvestigation(id, { signal: ac.signal })
            .then((inv) => { if (alive) setRead({ status: "done", activity: inv.activity, error: null }); })
            .catch((error) => { if (alive && error?.name !== "AbortError") setRead({ status: "error", activity: [], error }); });
        return () => { alive = false; ac.abort(); };
    }, [id, version]);
    return read;
}
